import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuth } from '@/auth/use-auth';
import {
  buildOtpAuthUri,
  confirmPassword,
  confirmTwoFactor,
  disableTwoFactor,
  enableTwoFactor,
  getRecoveryCodes,
  getTwoFactorSecret,
} from '@/auth/two-factor-api';
import { BevelWindow } from '@/components/prototype/bevel-window';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type Mode = 'idle' | 'confirming';

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function TwoFactorSection() {
  const { t } = useTranslation('auth');
  const { t: tc } = useTranslation('common');
  const { user, refresh } = useAuth();

  const enabled = Boolean(user?.two_factor_confirmed_at);
  const [mode, setMode] = useState<Mode>('idle');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [otpUri, setOtpUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setMode('idle');
    setPassword('');
    setCode('');
    setOtpUri(null);
    setSecret(null);
    setRecoveryCodes([]);
  };

  const handleEnable = async () => {
    if (password.trim().length === 0 || !user) return;
    setBusy(true);
    try {
      await confirmPassword(password);
      await enableTwoFactor();
      const { secretKey } = await getTwoFactorSecret();
      const codes = await getRecoveryCodes();
      setSecret(secretKey);
      setOtpUri(buildOtpAuthUri(secretKey, user.email));
      setRecoveryCodes(codes);
      setMode('confirming');
      setPassword('');
    } catch (error) {
      toast.error(messageOf(error, t('errors.generic')));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (code.trim().length === 0) return;
    setBusy(true);
    try {
      await confirmTwoFactor(code.trim());
      await refresh();
      toast.success(t('settings.twoFactorOn'));
      reset();
    } catch {
      toast.error(t('errors.invalidCode'));
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    if (password.trim().length === 0) return;
    setBusy(true);
    try {
      await confirmPassword(password);
      await disableTwoFactor();
      await refresh();
      toast.success(t('settings.twoFactorOff'));
      reset();
    } catch (error) {
      toast.error(messageOf(error, t('errors.generic')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <BevelWindow
      title={t('settings.twoFactor')}
      action={
        <Badge variant={enabled ? 'success' : 'default'}>
          {enabled ? t('settings.twoFactorOn') : t('settings.twoFactorOff')}
        </Badge>
      }
    >
      {mode === 'confirming' && otpUri ? (
        <div className="flex flex-col gap-4">
          <p className="font-mono text-xs text-dim">{t('settings.twoFactorScan')}</p>
          <div className="flex flex-wrap items-start gap-4">
            <div className="bevel bg-white p-2">
              <QRCodeSVG value={otpUri} size={148} />
            </div>
            {secret ? (
              <div className="flex flex-col gap-2">
                <span className="text-faint text-[10px] uppercase tracking-wide">
                  {tc('copy.secret')}
                </span>
                <code className="bevel-in bg-bar-bg px-2 py-1 font-mono text-xs text-orange break-all">
                  {secret}
                </code>
              </div>
            ) : null}
          </div>

          {recoveryCodes.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-faint text-[10px] uppercase tracking-wide">
                {t('settings.recoveryCodes')}
              </span>
              <div className="bevel-in bg-bar-bg grid grid-cols-2 gap-1 p-2 font-mono text-xs text-text">
                {recoveryCodes.map((rc) => (
                  <span key={rc}>{rc}</span>
                ))}
              </div>
              <p className="text-faint font-mono text-[11px]">{t('settings.recoveryHint')}</p>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="twofactor-confirm">{t('settings.twoFactorConfirm')}</Label>
            <Input
              id="twofactor-confirm"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="primary" onClick={handleConfirm} disabled={busy}>
              {t('twoFactor.submit')}
            </Button>
            <Button type="button" variant="ghost" onClick={reset} disabled={busy}>
              {tc('actions.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="twofactor-password">{t('fields.currentPassword')}</Label>
            <Input
              id="twofactor-password"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          {enabled ? (
            <div>
              <Button type="button" variant="destructive" onClick={handleDisable} disabled={busy}>
                {t('settings.twoFactorDisable')}
              </Button>
            </div>
          ) : (
            <div>
              <Button type="button" variant="primary" onClick={handleEnable} disabled={busy}>
                {t('settings.twoFactorEnable')}
              </Button>
            </div>
          )}
        </div>
      )}
    </BevelWindow>
  );
}
